// Doc Order — shared section context.
// Data (order slices, fees, derivedDocs, loan facts) flows to sections as PROPS
// from Workspace; the cross-cutting bits — editability, toasts, invoice-folder
// status, the render clock, and the autosave action helpers — flow through this
// context so sections don't prop-drill them. Editability is server-authoritative
// (viewer caps + stage), never derived in a component.
import { createContext, useContext, type ReactNode } from 'react';
import type {
  DocOrderFolderDocs,
  DocOrderRow,
  DocOrderResponse,
  DocOrderSettlement,
  DocOrderUploadTarget,
  DocOrderWritebacks
} from '@/services/docOrderService';
import type { VestingPatch } from './useDocOrder';
import type {
  DocOrderFee,
  DocOrderLoanFacts,
  DocOrderRole,
  DocOrderStage,
  DocOrderViewer,
  InsuranceInfo
} from './core';

// Type-only source for ClosingPatch (value is never read at runtime).
const _CLOSING_COLUMNS = [
  'signing_date',
  'closing_date',
  'first_payment_date',
  'power_of_attorney',
  'sign_type',
  'doc_email',
  'doc_dating'
] as const;

export type ClosingPatch = Partial<Pick<DocOrderRow, (typeof _CLOSING_COLUMNS)[number]>>;

export interface DocOrderContextValue {
  /** processor && draft && canEditDraft — the only editable combination in P1. */
  editable: boolean;
  stage: DocOrderStage;
  role: DocOrderRole;
  viewer: DocOrderViewer;
  loanFacts: DocOrderLoanFacts;
  /** order.closing_date — read by Insurance/Documents staleness helpers. */
  closingDate: string | null;
  /** Display name of the acting user — stamped onto acks/escalations. */
  userName: string;
  /** feeKey → the fee's invoice folder has ≥1 current doc in the eFolder mirror. */
  invoiceFolderStatus: Record<string, boolean>;
  /** feeKey → mapped folder name + its active files (the eye popover's data). */
  invoiceFolderDocs: Record<string, DocOrderFolderDocs>;
  /** Render-time clock passed to core staleness helpers (client uses new Date()). */
  now: Date;
  addToast: (message: string, icon?: string) => void;
  patchClosing: (patch: ClosingPatch) => void;
  patchSettlement: (patch: Partial<DocOrderSettlement>) => void;
  patchInsurance: (patch: Partial<InsuranceInfo>) => void;
  patchVesting: (patch: VestingPatch) => void;
  saveFee: (feeId: string, patch: Partial<DocOrderFee>) => void;
  addManualFee: (patch: { name: string; payee?: string | null; amount?: number }) => Promise<void>;
  /** Drain pending autosaves NOW (R4 #3/#20; R5: strict — TRUE only when
   *  drained with NO rejection and NO stall). */
  flush: () => Promise<boolean>;
  /** Awaited single-fee decision save (R5): patch factory evaluated from the
   *  CURRENT fee post-flush — the ReconcileSheet's reconcile/reduce paths. */
  saveFeeNow: (
    feeId: string,
    buildPatch: (fee: DocOrderFee) => Partial<DocOrderFee>
  ) => Promise<{ ok: boolean; status?: number; code?: string; error?: string }>;
  /** Server-owned typed escalation (kind derived server-side). */
  escalateFee: (feeId: string, note: string) => Promise<DocOrderResponse>;
  undoEscalation: (feeId: string) => Promise<DocOrderResponse>;
  /** R5 A2 — the closer's controlled First Payment override. */
  setFirstPaymentOverride: (input: { month: string; reason: string } | { clear: true }) => Promise<DocOrderResponse>;
  /** Autosave gave up without losing edits — drives the unsaved-changes banner. */
  saveStalled: boolean;
  // ---- P2+P3 convergence state + workflow actions consumed by the sections ----
  /** Grid write-back gate + pending-field count (the "queued/synced" chips). */
  writebacks: DocOrderWritebacks;
  /** true while any workflow transition is in flight — disables the action controls. */
  busy: boolean;
  /** Insurance defer-and-sign (A1.4) — used from the sign-off pad. */
  deferInsurance: (reason: string) => Promise<DocOrderResponse>;
  closingEditFee: (feeId: string, patch: Partial<DocOrderFee>) => Promise<DocOrderResponse>;
  approveFee: (feeId: string, approve: boolean) => Promise<DocOrderResponse>;
  vestingAttest: () => Promise<DocOrderResponse>;
  retryWrites: () => Promise<DocOrderResponse>;
  setLenderCredit: (amount: number) => Promise<DocOrderResponse>;
  escalateLenderCredit: (amount: number) => Promise<DocOrderResponse>;
  withdrawLenderCreditEscalation: () => Promise<DocOrderResponse>;
  loRequestChanges: (
    note: string,
    items: Array<{ id: string; toAmount: number; toReimburse: boolean }>
  ) => Promise<DocOrderResponse>;
  loAcknowledge: () => Promise<DocOrderResponse>;
  downloadPdf: () => Promise<void>;
  // ---- P4 uploads / mark-current / reset (gated by writebacks.enabled) ----
  /** Multipart upload of an invoice/doc; the resolved response carries `upload.verdict`. */
  uploadFile: (target: DocOrderUploadTarget, file: File, fileName: string) => Promise<DocOrderResponse>;
  /** Commit a fee's live Mark-Current preview to the eFolder. */
  commitMarks: (feeId: string) => Promise<DocOrderResponse>;
  /** Reopen a cleared §6 checklist doc for a replacement upload. */
  resetDocument: (checklistId: string) => Promise<DocOrderResponse>;
  /** Remove a user-added / auto-injected fee (manual or auto_trust only). */
  deleteFee: (feeId: string) => Promise<DocOrderResponse>;
  /** Converge queued upload entries NOW (Batch 3.1 F4 — the "Check status"
   *  affordance next to a queued/held upload). Never the full refresh. */
  checkUploadStatus: () => Promise<void>;
  /** Shared in-flight state — every Check-status button disables while one runs. */
  checkingUploads: boolean;
}

const DocOrderContext = createContext<DocOrderContextValue | null>(null);

export function DocOrderProvider({ value, children }: { value: DocOrderContextValue; children: ReactNode }) {
  return <DocOrderContext.Provider value={value}>{children}</DocOrderContext.Provider>;
}

export function useDocOrderCtx(): DocOrderContextValue {
  const ctx = useContext(DocOrderContext);
  if (!ctx) {
throw new Error('useDocOrderCtx must be used within a DocOrderProvider');
}
  return ctx;
}
